import { useQuery } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import type { ListResult, ProductListItem } from "@cx/contracts";
import { formatBRL } from "@cx/domain";
import { useSession } from "../../session";

/**
 * Busca de produto para a aba Composição: só produtos simples, inclusive as variações (o pai de variação e o kit não
 * podem ser componentes). Usa a mesma busca FTS da lista.
 */
export function ProductPicker({
  exclude,
  onPick,
  label = "Adicionar componente",
  inputId = "component-search",
  target = "ao kit",
}: {
  exclude: string[];
  onPick(p: ProductListItem): void;
  /** Rótulo da busca, id do campo e destino no "Adicionar X …" (a Estrutura de produção reaproveita a busca). */
  label?: string;
  inputId?: string;
  target?: string;
}): ReactElement {
  const { companyId, api } = useSession();
  const [q, setQ] = useState("");
  const term = q.trim();
  const found = useQuery({
    queryKey: ["products", companyId, { q: term, picker: "component" }],
    enabled: term.length >= 2,
    queryFn: () =>
      api<ListResult<ProductListItem>>(
        `/products?${new URLSearchParams({ q: term, format: "S", includeVariations: "yes", limit: "8" })}`,
      ),
  });
  const rows = (found.data?.data ?? []).filter((p) => !exclude.includes(p.id));
  return (
    <div className="mb-3">
      <label className="form-label" htmlFor={inputId}>
        {label}
      </label>
      <input
        id={inputId}
        type="search"
        className="form-control"
        placeholder="Digite nome, SKU ou GTIN do produto"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {term.length >= 2 && (
        <ul className="list-group mt-1" aria-label="Produtos encontrados">
          {found.isLoading && <li className="list-group-item text-muted">Buscando…</li>}
          {found.isSuccess && rows.length === 0 && (
            <li className="list-group-item text-muted">Nenhum produto simples encontrado.</li>
          )}
          {rows.map((p) => (
            <li key={p.id} className="list-group-item d-flex align-items-center gap-2">
              <span className="flex-grow-1">
                {p.name}
                {p.sku && <span className="text-muted small ms-2">{p.sku}</span>}
                {p.costCents !== undefined && (
                  <span className="text-muted small ms-2">custo {formatBRL(p.costCents)}</span>
                )}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                aria-label={`Adicionar ${p.name} ${target}`}
                onClick={() => {
                  onPick(p);
                  setQ("");
                }}
              >
                Adicionar
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
